/**
 * ViewerFallback — shared "can't preview this file" state for the Workspace.
 *
 * Shown for legacy .doc files (no browser renderer), unknown/corrupt files,
 * and any viewer that fails to render. Always offers a download-original
 * fallback built from the already-fetched file bytes (REQ-18).
 */
import { Download01, File06 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icons";

interface ViewerFallbackProps {
    /** Headline, e.g. "Preview not available" */
    title: string;
    /** Explanation of why the preview can't render */
    message: string;
    /** File name shown under the message (optional) */
    fileName?: string;
    /** Downloads the original file; button hidden when omitted */
    onDownload?: () => void;
}

export function ViewerFallback({ title, message, fileName, onDownload }: ViewerFallbackProps) {
    // Self-padded: the Workspace canvas is edge-to-edge, so the card brings its own gutter
    return (
        <div className="custom-scrollbar flex h-full w-full items-start justify-center overflow-y-auto p-6">
            <div className="flex aspect-[1/1.414] w-full max-w-4xl flex-col items-center justify-center gap-4 rounded-lg border border-secondary bg-primary p-8 shadow-xl">
                <FeaturedIcon icon={File06} color="gray" theme="light" size="xl" />
                <div className="text-center">
                    <p className="text-sm font-semibold tracking-wider text-tertiary uppercase">{title}</p>
                    <p className="mt-1 text-xs text-quaternary">{message}</p>
                    {fileName && <p className="mt-2 max-w-md truncate text-xs font-medium text-tertiary">{fileName}</p>}
                </div>
                {onDownload && (
                    <Button size="md" color="secondary" iconLeading={Download01} onClick={onDownload}>
                        Download original
                    </Button>
                )}
            </div>
        </div>
    );
}
